'use client';

import { setLoginTime } from '@/@shared/middleware/middleware';
import { ErrorKeys } from '@/@shared/types/errorKeys';
import { useCreateAuthSigninWebEmailMutation } from '@/domains/auth/api/auth/useAuthMutations';
import type { SigninEmailRO } from '@/domains/auth/types/deepSchema';
import type { AuthController_signinEmailWeb_Body } from '@/domains/auth/types/validated';
import { LinkedBusinessRefresh } from '@/legacy/apis/repository/auth';
import { putOwnerUser } from '@/model/dexie/ownerDexie';
import { saveUserDataToDexie, tokenUpdate } from '@/model/dexie/userDexie';
import { signInSchema, type SignInType } from '@/model/zod/authZod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';

export const useLoginForm = (locale: string) => {
	const router = useRouter();

	// React Hook Form
	const methods = useForm<SignInType>({
		criteriaMode: 'all',
		mode: 'onChange',
		resolver: zodResolver(signInSchema),
	});

	// Mutation - domains에서 가져온 것
	const loginMutation = useCreateAuthSigninWebEmailMutation();

	// 로그인 처리
	const submitLogin = async (formData: SignInType) => {
		// 이전 에러 클리어
		methods.clearErrors();

		const body: AuthController_signinEmailWeb_Body = {
			email: formData.email,
			password: formData.password,
		};

		const result = await loginMutation.mutateAsync(body);

		// ⭐ 에러 처리 (throwOnError: false이므로 result.error에 담김)
		if ('error' in result && result.error) {
			const errorData = result.error as { error?: string; message?: string };

			// EMAIL_NOT_EXIST 에러인 경우
			if (errorData.error === 'EMAIL_NOT_EXIST') {
				methods.setError('email', {
					type: 'manual',
					message: ErrorKeys.server.emailNotExist,
				});
				return false;
			}

			// PASSWORD_FAILED 에러인 경우
			if (errorData.error === 'PASSWORD_FAILED') {
				methods.setError('password', {
					type: 'manual',
					message: ErrorKeys.server.passwordFailed,
				});
				return false;
			}

			// 기타 에러는 password 필드에 표시
			methods.setError('password', {
				type: 'manual',
				message: ErrorKeys.server.unknownError,
			});
			return false;
		}

		// ⭐ 성공 처리
		if (!result?.data) {
			return false;
		}

		// 로그인 시간 기록 (토큰 삭제 방지용)
		setLoginTime();

		// 1. 사용자 정보 저장
		await saveUserDataToDexie(result.data as unknown as SigninEmailRO);
		await putOwnerUser();

		// 2. 토큰 저장 (완료될 때까지 대기)
		if ('response' in result && result.response instanceof Response) {
			await tokenUpdate(result.response);
		}

		// 3. 비즈니스 계정 조회 (완료될 때까지 대기)
		await LinkedBusinessRefresh();

		// 4. 모든 초기화가 완료된 후 페이지 이동
		router.push('/');

		return true;
	};

	return {
		// Form methods
		methods,

		// Actions
		submitLogin,

		// Loading state
		isSubmitting: loginMutation.isPending,
	};
};
